import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "./firebase";
import { Property, Kiosk, PropertyAddress } from "@/types";

class FirebaseService {
  private static instance: FirebaseService;

  public static getInstance(): FirebaseService {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  /**
   * Fetch all active properties from Firestore
   */
  async getProperties(): Promise<Property[]> {
    try {
      if (!db) {
        throw new Error("Firestore database not initialized");
      }

      const propertiesRef = collection(db, "properties");
      
      // Try with orderBy first, fallback to unordered if index is missing
      let querySnapshot;
      try {
        const q = query(propertiesRef, orderBy("name"));
        querySnapshot = await getDocs(q);
      } catch (orderError) {
        // If orderBy fails due to missing index, try without ordering
        if (
          orderError &&
          typeof orderError === "object" &&
          "code" in orderError &&
          orderError.code === "failed-precondition"
        ) {
          console.warn(
            "⚠️ Firestore index missing for 'name' field. Fetching without ordering."
          );
          querySnapshot = await getDocs(propertiesRef);
        } else {
          throw orderError;
        }
      }

      const properties: Property[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Only include active properties
        if (data.active !== false) {
          properties.push({
            id: doc.id,
            name: data.name,
            kiosks: data.kiosks || [],
            active: data.active !== false,
            address: data.address as PropertyAddress,
            collectionId: data.collectionId || undefined,
          });
        }
      });

      // Sort in memory if we couldn't use orderBy
      properties.sort((a, b) => a.name.localeCompare(b.name));

      return properties;
    } catch (error) {
      console.error("❌ Error fetching properties:", error);
      
      // Provide more specific error messages
      const errorObj =
        error && typeof error === "object" && "code" in error
          ? (error as { code?: string; message?: string })
          : null;

      if (errorObj?.code === "permission-denied") {
        throw new Error(
          "Permission denied. Please check Firestore security rules allow read access to 'properties' collection."
        );
      }
      if (errorObj?.message?.includes("not initialized")) {
        throw new Error(
          "Firebase not initialized. Please check your environment variables (VITE_FIREBASE_*)."
        );
      }
      if (
        errorObj?.code === "unavailable" ||
        errorObj?.code === "deadline-exceeded"
      ) {
        throw new Error(
          "Firestore is temporarily unavailable. Please check your internet connection and try again."
        );
      }

      const errorMessage =
        error instanceof Error
          ? error.message
          : errorObj?.message || errorObj?.code || "Unknown error";

      throw new Error(`Failed to fetch properties: ${errorMessage}`);
    }
  }

  /**
   * Fetch all active kiosks from all active properties
   */
  async getKiosks(): Promise<Kiosk[]> {
    try {
      const properties = await this.getProperties();
      const kiosks: Kiosk[] = [];

      properties.forEach((property) => {
        // Only process active properties
        if (property.active) {
          property.kiosks.forEach((kioskData) => {
            // Only include active kiosks
            if (kioskData.active) {
              kiosks.push({
                id: kioskData.id,
                name: kioskData.name,
                propertyId: property.id,
                propertyName: property.name,
                active: kioskData.active,
              });
            }
          });
        }
      });

      return kiosks;
    } catch (error) {
      console.error("❌ Error fetching kiosks:", error);
      // Re-throw the error from getProperties with more context
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      throw new Error(`Failed to fetch kiosks: ${errorMessage}`);
    }
  }

  /**
   * Get active kiosks for a specific property
   */
  async getKiosksByProperty(propertyId: string): Promise<Kiosk[]> {
    try {
      const properties = await this.getProperties();
      const property = properties.find((p) => p.id === propertyId);

      if (!property || !property.active) {
        throw new Error("Property not found or inactive");
      }

      return property.kiosks
        .filter((kioskData) => kioskData.active)
        .map((kioskData) => ({
          id: kioskData.id,
          name: kioskData.name,
          propertyId: property.id,
          propertyName: property.name,
          active: kioskData.active,
        }));
    } catch (error) {
      console.error("Error fetching kiosks by property:", error);
      throw new Error("Failed to fetch kiosks for property");
    }
  }

  /**
   * Get a specific property by ID (only if active)
   */
  async getProperty(propertyId: string): Promise<Property | null> {
    try {
      const propertyRef = doc(db, "properties", propertyId);
      const propertySnap = await getDoc(propertyRef);

      if (propertySnap.exists()) {
        const data = propertySnap.data();
        // Only return if property is active
        if (data.active !== false) {
          return {
            id: propertySnap.id,
            name: data.name,
            address: data.address as PropertyAddress,
            kiosks: data.kiosks || [],
            active: data.active !== false,
            collectionId: data.collectionId || undefined,
          };
        }
      }

      return null;
    } catch (error) {
      console.error("Error fetching property:", error);
      throw new Error("Failed to fetch property");
    }
  }
}

export default FirebaseService;
